import React, { useEffect, useRef } from "react";
import { getMessagingInstance, getToken, onMessage, isSupported } from "./firebase";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch } from "react-redux";
import { setRefetchSpecificUser } from "../redux/multicommunity/multicommunity";
import { isOAuthReturnUrl } from "../helpers/exchangeOAuth";
import { isOAuthBootstrapInFlight } from "../helpers/exchangeOAuthBootstrap";

const shouldDeferFcm = () =>
  isOAuthReturnUrl() || isOAuthBootstrapInFlight();

const VAPID_KEY = "BGgPTIKkjVJXD6Sc94ZO-Q82Fi5VHnph7dluZSjXFlnyqrA9sZrbfWfShaibhhHWRzzATGqw22foQ7VVDj0W0Xg";

const NotificationSample = () => {
  const dispatch = useDispatch();
  const tokenSentRef = useRef(false);
  const setupAttemptedRef = useRef(false);

  // Function to send FCM token to backend
  const sendTokenToBackend = async (fcmToken) => {
    try {
      const authToken = localStorage.getItem("token");
      if (!authToken) {
        // console.warn("⚠️ No auth token found, skipping FCM token update");
        return false;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}fcm/updateFcmToken`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authToken,
          },
          body: JSON.stringify({ fcmToken }),
        }
      );

      const data = await response.json();

      if (response.ok) {
       
        localStorage.setItem("fcm_token", fcmToken);
        localStorage.setItem("fcm_token_time", Date.now().toString());
        tokenSentRef.current = true;
        dispatch(setRefetchSpecificUser(true));
        return true;
      } else {
        
        return false;
      }
    } catch (err) {
      // console.error("❌ Failed to send FCM token to backend:", err);
      return false;
    }
  };

  // Request notification permission and setup FCM
  const setupFCM = async () => {
    try {
      if (shouldDeferFcm()) {
        return;
      }

      // Prevent multiple setup attempts
      if (setupAttemptedRef.current) {
        // console.log("⚠️ FCM setup already attempted");
        return;
      }
      setupAttemptedRef.current = true;

      // Check if user is logged in
      const authToken = localStorage.getItem("token");
      if (!authToken) {
        // console.log("⚠️ User not logged in, skipping FCM setup");
        return;
      }

      // Check if messaging is supported
      const messagingSupported = await isSupported();
      if (!messagingSupported) {
        // console.warn("⚠️ Firebase Messaging not supported in this browser");
        return;
      }

      // ✅ STEP 1: Request notification permission
      // This is needed to generate FCM token
      // console.log("🔔 Requesting notification permission...");
      
      if (!("Notification" in window)) {
        // console.warn("⚠️ This browser does not support notifications");
        return;
      }

      const permission = await Notification.requestPermission();
      // console.log("📱 Notification permission:", permission);

      if (permission !== "granted") {
        // console.warn("⚠️ Notification permission not granted");
        // console.log("💡 Permission is needed to generate FCM token (even though we only show toasts)");
        
        // toast.warning(
        //   "Please allow notifications to receive updates. We'll only show toast notifications, not browser notifications!",
        //   { autoClose: 8000, position: "top-center" }
        // );
        return;
      }

      // ✅ STEP 2: Initialize Firebase Messaging
      const messaging = await getMessagingInstance();
      if (!messaging) {
        // console.error("❌ Failed to initialize Firebase Messaging");
        return;
      }

      // ✅ STEP 3: Register service worker
      let swRegistration = null;
      try {
        if ("serviceWorker" in navigator) {
          swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
          // console.log("✅ Service Worker registered");
          await navigator.serviceWorker.ready;
        }
      } catch (swError) {
        // console.error("❌ Service Worker registration failed:", swError);
        // console.log("⚠️ Continuing without service worker");
      }

      // ✅ STEP 4: Get FCM token
      try {
        const currentToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swRegistration,
        });

        if (currentToken) {
          // console.log("✅ FCM Token generated:", currentToken);

          // Send token to backend
          const storedToken = localStorage.getItem("fcm_token");
          if (storedToken !== currentToken || !tokenSentRef.current) {
            const success = await sendTokenToBackend(currentToken);
            if (success) {
              // toast.success("✅ Notifications enabled! You'll receive toast updates.", {
              //   autoClose: 3000,
              //   position: "top-right",
              // });
            }
          }

          // ✅ STEP 5: Setup foreground message listener
          // This is where toast notifications are triggered
          onMessage(messaging, (payload) => {
            // console.log("🎉 Foreground notification received:", payload);

            // Extract data from payload
            // Since we're sending data-only messages, look in payload.data
            const data = payload?.data || {};
            const notification = payload?.notification || {};

            const title = data.title || notification.title || "New Notification";
            const body = data.body || notification.body || "";

            // console.log(`📩 Showing toast: ${title} - ${body}`);

            // Show custom toast notification (NOT browser notification)
            if (title || body) {
              toast.info(
                <div>
                  <strong>{title}</strong>
                  {body && <div style={{ marginTop: "4px" }}>{body}</div>}
                </div>,
                {
                  position: "top-right",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                }
              );

              // Dispatch custom event for NotificationDropdown
              const event = new CustomEvent("fcm-notification", {
                detail: {
                  title,
                  body,
                  time: new Date().toLocaleString(),
                  data,
                  payload,
                },
              });
              window.dispatchEvent(event);

              // Trigger data refetch
              dispatch(setRefetchSpecificUser(true));
            } else {
              // console.warn("⚠️ Received notification without title/body:", payload);
            }
          });

          // console.log("✅ FCM setup complete!");
          // console.log("✅ You'll receive TOAST notifications (no browser notifications)");
        } else {
          // console.warn("⚠️ Failed to generate FCM token");
          
          // toast.error("Failed to set up notifications. Please refresh the page.", {
          //   autoClose: 5000,
          //   position: "top-center",
          // });
        }
      } catch (tokenError) {
        // console.error("❌ Error getting FCM token:", tokenError);
        
        if (tokenError.code === "messaging/permission-blocked") {
          // toast.warning(
          //   "Notifications blocked. Please enable them in your browser settings.",
          //   { autoClose: 5000, position: "top-center" }
          // );
        }
      }
    } catch (error) {
      //  console.error("❌ Error setting up FCM:", error);
    }
  };

  useEffect(() => {
    // Setup FCM when component mounts
    setupFCM();

    // Handle visibility change (when user returns to tab)
    const handleVisibilityChange = () => {
      if (shouldDeferFcm()) {
        return;
      }

      if (document.visibilityState === "visible") {
        const authToken = localStorage.getItem("token");
        const storedToken = localStorage.getItem("fcm_token");
        const tokenTime = localStorage.getItem("fcm_token_time");

        // If logged in but no token, or token is old (> 24 hours), refresh
        if (authToken && (!storedToken || !tokenTime)) {
          // console.log("🔄 No token found, setting up FCM...");
          setupAttemptedRef.current = false;
          setupFCM();
        } else if (authToken && tokenTime) {
          const hoursSinceToken = (Date.now() - parseInt(tokenTime)) / (1000 * 60 * 60);
          if (hoursSinceToken > 24) {
            // console.log("🔄 Token is old, refreshing...");
            setupAttemptedRef.current = false;
            setupFCM();
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  return null; // No UI needed
};

export default NotificationSample;

